import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Mail, Phone, MapPin, Send, Github, Linkedin, Globe } from 'lucide-react';
import SectionHeading from './SectionHeading';

const Contact: React.FC = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  const contactInfo = [
    {
      icon: Mail,
      label: 'Email',
      value: 'shivanshmane2004@gmail.com',
      href: 'mailto:shivanshmane2004@gmail.com'
    },
    {
      icon: Phone,
      label: 'Phone',
      value: '+1 (765) 712-2620',
      href: 'tel:+17657122620'
    },
    {
      icon: MapPin,
      label: 'Location',
      value: 'Greencastle, IN, 46135',
      href: null
    }
  ];

  const socialLinks = [
    {
      icon: Github,
      label: 'GitHub',
      href: 'https://github.com/ShivanshMane',
      color: 'hover:text-gray-400'
    },
    {
      icon: Linkedin,
      label: 'LinkedIn',
      href: 'https://linkedin.com/in/shivanshmane1122',
      color: 'hover:text-blue-400'
    },
    {
      icon: Globe,
      label: 'Website',
      href: 'https://maneshivansh.com',
      color: 'hover:text-cosmic-aurora-emerald'
    }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission here
    console.log('Form submitted:', formData);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="relative py-24">
      <div className="section-shell">
        <div ref={ref}>
          <SectionHeading
            eyebrow="Contact"
            title={<>Let&apos;s create something <span className="gradient-text-vibrant">worth remembering.</span></>}
            description="I&apos;m open to new grad opportunities, internships, collaborations, and ambitious product work."
          />
        </div>

        <div className="grid gap-8 lg:grid-cols-[0.95fr_1.05fr]">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-8"
          >
            <div className="surface-panel surface-glow">
              <h3 className="mb-6 text-2xl font-bold gradient-text">
                Let's Connect
              </h3>
              <p className="mb-8 text-[1.02rem] leading-8 text-slate-700 dark:text-slate-300">
                I'm open to collaborations, internships, and freelance work. Feel free to say hi 
                or ask about any project you see! I'm always excited to discuss new opportunities 
                and innovative ideas.
              </p>

              <div className="space-y-4">
                {contactInfo.map((info, index) => (
                  <motion.div
                    key={info.label}
                    initial={{ opacity: 0, x: -20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.5, delay: 0.4 + index * 0.1 }}
                    className="glass flex items-center space-x-4 rounded-2xl px-4 py-4"
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-cosmic-aurora-emerald to-cosmic-aurora-sapphire">
                      <info.icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 dark:text-white">{info.label}</p>
                      {info.href ? (
                        <a
                          href={info.href}
                          className="text-slate-700 transition-colors duration-300 hover:text-cosmic-aurora-emerald dark:text-slate-300"
                        >
                          {info.value}
                        </a>
                      ) : (
                        <p className="text-slate-700 dark:text-slate-300">{info.value}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>

              <div className="pt-8">
                <h4 className="mb-4 font-semibold text-slate-900 dark:text-white">Follow Me</h4>
                <div className="flex gap-4">
                  {socialLinks.map((social, index) => (
                    <motion.a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      initial={{ opacity: 0, scale: 0 }}
                      animate={isInView ? { opacity: 1, scale: 1 } : {}}
                      transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                      whileHover={{ scale: 1.08, y: -2 }}
                      whileTap={{ scale: 0.9 }}
                      className={`glass flex h-12 w-12 items-center justify-center rounded-full text-slate-700 transition-all duration-300 hover:shadow-aurora-emerald dark:text-slate-300 ${social.color}`}
                    >
                      <social.icon size={20} />
                    </motion.a>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <div className="surface-panel surface-glow rounded-[28px] border border-white/10">
              <h3 className="mb-6 text-2xl font-bold gradient-text">
                Send Message
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.6 }}
                >
                  <label className="mb-2 block font-medium text-slate-900 dark:text-white">
                    Your Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 placeholder-slate-500 transition-all duration-300 focus:border-cosmic-aurora-emerald focus:outline-none focus:shadow-aurora-emerald dark:border-white/10 dark:bg-white/5 dark:text-slate-100"
                    placeholder="Enter your name"
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.7 }}
                >
                  <label className="mb-2 block font-medium text-slate-900 dark:text-white">
                    Your Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 placeholder-slate-500 transition-all duration-300 focus:border-cosmic-aurora-emerald focus:outline-none focus:shadow-aurora-emerald dark:border-white/10 dark:bg-white/5 dark:text-slate-100"
                    placeholder="Enter your email"
                  />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.8 }}
                >
                  <label className="mb-2 block font-medium text-slate-900 dark:text-white">
                    Your Message
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={5}
                    className="w-full resize-none rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-slate-900 placeholder-slate-500 transition-all duration-300 focus:border-cosmic-aurora-emerald focus:outline-none focus:shadow-aurora-emerald dark:border-white/10 dark:bg-white/5 dark:text-slate-100"
                    placeholder="Enter your message..."
                  />
                </motion.div>

                <motion.button
                  type="submit"
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.9 }}
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  className="btn-primary w-full"
                >
                  <span>Send Message</span>
                  <Send size={20} />
                </motion.button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
